<template>
	<div class="w-full">
		<GridLayout
			:sidebar-state="sidebarState"
			:reactive-sidebar="false"
		>
			<template #right-header>
				<div class="w-full h-full flex items-center gap-3">
					<button
						class="btn btn-rounded"
						@click="Previous"
					>
						<i class="fas fa-chevron-left"></i>
					</button>
					<Steps
						:steps-map="stepsMap"
						:current-step="currentStep"
						@select-step="
							currentStep = $event
							autoNav = false
						"
					/>
					<FavoriteRoute />
				</div>
			</template>
			<template #right-tool-bar>
				<Subtitle :value="subtitle" />

				<ToolBar v-if="step === 'confirm'">
					<div class="w-full flex items-center justify-center gap-4">
						<button
							id="payment-btn"
							class="btn btn-light btn-fixed-size px-4"
							type="button"
							@click="$refs.confirm && $refs.confirm.OpenModal('#payment-methods-modal')"
						>
							PAGAMENTO
						</button>
					</div>
				</ToolBar>
			</template>
			<template #right-content>
				<transition
					name="fade"
					mode="out-in"
				>
					<div v-if="step === 'pre'"></div>

					<Modules
						v-if="step === 'module'"
						class="w-full flex-1 flex flex-col py-4"
						type="strict"
						:module_id="scheduling.module_id"
						@next="WalkStep"
					/>

					<Modalities
						v-if="step === 'modality'"
						class="w-full flex-1 flex flex-col py-4"
						type="strict"
						:module_id="scheduling.module_id"
						:modality_id="scheduling.modality_id"
						@next="WalkStep"
					/>

					<MainContent v-if="step === 'patient'">
						<PatientAvatar :avatar="scheduling.patient?.avatar" />

						<PatientForm
							ref="patient_form"
							class="w-full flex-1 flex flex-col px-4"
							origin="fit"
							:patient="scheduling.patient"
							:with-query="true"
							@loaded="PatientFormLoaded"
							@next="WalkStep"
							@selected="scheduling.patient = $event"
						/>
					</MainContent>

					<Partnership
						v-if="step === 'partnership'"
						ref="partnership"
						class="w-full flex-1 flex flex-col"
						:modality_id="scheduling.modality_id"
						:partnership="scheduling.partnership"
						@next="WalkStep"
						@partnership-change="selectedPartnershipId = $event"
						@on-load-partnerships="partnerships = $event"
					/>

					<Guide
						v-if="step === 'guide'"
						ref="guide"
						class="w-full flex-1 flex flex-col"
						:modality_id="scheduling.modality_id"
						:partnership="scheduling.partnership"
						:partnerships="partnerships"
						:billing="scheduling.billing"
						:indicator="scheduling.indicator"
						:patient="scheduling.patient"
						:origin="scheduling.origin"
						@next="WalkStep"
						@billing-change="scheduling.billing = $event"
						@indicator-change="scheduling.indicator = $event"
					/>

					<Proceedings
						v-if="step === 'proceedings'"
						ref="proceedings"
						:modality_id="scheduling.modality_id"
						:partnership="scheduling.partnership"
						:proceedings="scheduling.proceedings"
						@next="WalkStep"
					/>

					<MainContent v-if="step === 'schedules'">
						<Schedules
							class="w-full flex-1 flex flex-col py-4"
							type="fit"
							:modality_id="scheduling.modality_id"
							:proceedings="scheduling.proceedings"
							:schedules="scheduling.schedules"
							@next="WalkStep"
						/>
					</MainContent>

					<AttendancePriority
						v-if="step === 'attendance_priority'"
						ref="attendance_priority"
						class="w-full flex-1 flex flex-col py-4"
						:classification="scheduling.classification"
						:billing="scheduling.billing"
						@next="WalkStep"
					/>

					<!-- :class="!printStatement ? 'pb-3' : 'pb-1'" -->
					<MainContent v-if="step === 'confirm'">
						<Confirm
							ref="confirm"
							class="w-full flex-1 flex flex-col py-4"
							type="fit"
							:module_id="scheduling.module_id"
							:modality_id="scheduling.modality_id"
							:partnership="scheduling.partnership"
							:proceedings="scheduling.proceedings"
							:time="scheduling.time"
							:patient="scheduling.patient"
							:classification="scheduling.classification"
							:billing="scheduling.billing"
							:origin="scheduling.origin"
							:print-statement="printStatement"
							:indicator="scheduling.indicator"
							@indicator="SetIndicator"
							@next="WalkStep"
						/>
					</MainContent>
				</transition>
			</template>
			<template #right-footer>
				<div class="w-full h-full flex items-center gap-3 justify-start">
					<button
						v-if="isReceptionFlow"
						class="btn btn-danger btn-fixed-size"
						:disabled="reception_flow_data?.is_finished"
						@click="CancelFlow()"
					>
						SUSPENDER
					</button>
				</div>
				<div class="w-full h-full flex items-center gap-3 justify-center">
					<button
						class="btn btn-fixed-size"
						:disabled="step !== 'patient'"
						@click="
							$refs.patient_form.ClearForm()
							scheduling.patient = null
						"
					>
						LIMPAR
					</button>
					<button
						v-if="isReceptionFlow"
						class="btn btn-fixed-size"
						:disabled="patientsList.length !== 1 || !reception_flow_data?.is_finished"
						@click="NewFitToPatient"
					>
						NOVO ENCAIXE
					</button>
				</div>
				<div class="w-full h-full flex items-center gap-3 justify-end">
					<button
						class="btn btn-success btn-fixed-size"
						type="button"
						:disabled="continueButtonDisabled"
						@click="Continue"
					>
						{{ finishButtonLabel }}
					</button>
				</div>
			</template>
		</GridLayout>
	</div>
</template>

<script>
	const _ = window._
	import Swal from 'sweetalert2'
	import { mapGetters, mapActions } from 'vuex'
	import { useConfirmation, useError } from '@/composables'
	import receptionFlowMixin from '@/mixins/receptionFlow'

	import Modules from '@/components/shared/Modules'
	import Modalities from '@/components/shared/Modalities'
	import Proceedings from '@/components/shared/scheduling/Proceedings'
	import Partnership from '@/components/shared/scheduling/Partnership'
	import Guide from '@/components/shared/scheduling/Guide'
	import Schedules from '@/components/shared/scheduling/Schedules'
	import Patient from '@/components/shared/scheduling/Patient'
	import AttendancePriority from '@/components/shared/scheduling/AttendancePriority'
	import Confirm from '@/components/shared/scheduling/Confirm'
	import PatientAvatar from '@/components/shared/patient/components/PatientAvatar'

	export default {
		name: 'FitWizard',
		mixins: [receptionFlowMixin],
		components: {
			Modules,
			Modalities,
			Partnership,
			Guide,
			Proceedings,
			AttendancePriority,
			Schedules,
			Patient,
			Confirm,
			PatientAvatar,
		},
		props: {
			fit_scheduling: {
				type: Object,
				default: null,
			},
		},
		data() {
			return {
				default_avatar: require('@/assets/img/profile.png'),
				patients: [],
				partnerships: [],
				scheduling: {
					module_id: null,
					modality_id: null,
					schedules: [],
					proceedings: [],
					partnership: null,
					place: null,
					time: null,
					patient: null,
					classification: 1,
					billing: null,
					obs: '',
					indicator: null,
					payment_info: null,
					origin: 4, // ORIGIN_FIT
					instructions: '',
				},
				printStatement: false,
				sidebarState: false,
				showSearchBar: false,
				selectedPartnershipId: null,
				patientsList: [],
				currentStep: 0,
				stepsMap: [
					{
						key: 'pre',
					},
					{
						key: 'module',
						number: 1,
						label: 'MÓDULO',
						isValid: false,
					},
					{
						key: 'modality',
						number: 2,
						label: 'MODALIDADE',
						isValid: false,
					},
					{
						key: 'patient',
						number: 3,
						label: 'CADASTRO',
						isValid: false,
					},
					{
						key: 'partnership',
						number: 4,
						label: 'CONVÊNIO',
						isValid: false,
					},
					{
						key: 'guide',
						number: 5,
						label: 'GUIA',
						isValid: false,
					},
					{
						key: 'proceedings',
						number: 6,
						label: 'PROCEDIMENTOS',
						isValid: false,
					},
					{
						key: 'schedules',
						number: 7,
						label: 'AGENDA',
						isValid: false,
					},
					{
						key: 'attendance_priority',
						number: 8,
						label: 'PRIORIDADE',
						isValid: false,
					},
					{
						key: 'confirm',
						number: 9,
						label: 'ENCAIXAR',
						isValid: false,
					},
				],
				finishButtonLabel: 'CONTINUAR',
				autoNav: true,
			}
		},
		computed: {
			...mapGetters('auth', ['requester_selected_id']),
			...mapGetters('system', ['showSidebar', 'modules', 'modalities']),
			step() {
				return this.stepsMap[this.currentStep].key
			},
			continueButtonDisabled() {
				switch (this.step) {
					case 'patient':
						return false
					case 'partnership':
						return !this.selectedPartnershipId
					case 'guide':
						return false
					case 'proceedings':
						return this.scheduling.proceedings.length === 0
					case 'attendance_priority':
					case 'confirm':
						return false
					default:
						return true
				}
			},
			subtitle() {
				let subtitle = 'MÓDULO'

				if (this.scheduling.modality_id) {
					const modality = this.modalities.find(m => m.id === this.scheduling.modality_id)
					if (modality) subtitle = modality.name
				} else if (this.scheduling.module_id) {
					const module = this.modules.find(m => m.id === this.scheduling.module_id)
					if (module) subtitle = module.name
				}

				return subtitle
			},
		},
		watch: {
			step: {
				handler(val) {
					let hash = ''

					switch (val) {
						case 'module':
							hash = '#modulo'
							this.modality_id = null
							break
						case 'modality':
							hash = '#modalidade'
							break
						case 'patient':
							hash = '#paciente'
							break
						case 'partnership':
							if (this.isReceptionFlow) {
								this.reception_flow_data.is_finished = false
								this.finishButtonLabel = 'CONTINUAR'
							}

							hash = '#convenio'
							break
						case 'guide':
							hash = '#guia'
							break
						case 'proceedings':
							hash = '#procedimentos'
							break
						case 'schedules':
							hash = '#horarios'
							break
						case 'attendance_priority':
							hash = '#prioridade-de-atendimento'
							break
						case 'confirm':
							hash = '#confirmacao'
							break
					}

					window.location.hash = hash
				},
				immediate: true,
			},
			$route(to) {
				switch (to.hash) {
					case '#modulo':
						this.currentStep = 1
						this.modality_id = null
						break
					case '#modalidade':
						this.currentStep = 2
						break
					case '#paciente':
						this.currentStep = 3
						break
					case '#convenio':
						this.currentStep = 4
						break
					case '#guia':
						this.currentStep = 5
						break
					case '#procedimentos':
						this.currentStep = 6
						break
					case '#horarios':
						this.currentStep = 7
						break
					case '#prioridade-de-atendimento':
						this.currentStep = 8
						break
					case '#confirmacao':
						this.currentStep = 9

						this.finishButtonLabel = 'ENCAIXAR'
						break
				}
			},
		},
		methods: {
			...mapActions('system', ['StartLoading', 'FinishLoading', 'GetModulesAndModalities']),
			Previous() {
				switch (this.step) {
					case 'module':
						this.$router.push({ name: 'Home' })
						break
					case 'modality':
					case 'patient':
					case 'partnership':
					case 'guide':
					case 'proceedings':
					case 'schedules':
					case 'attendance_priority':
					case 'confirm':
						this.currentStep--
						break
					default:
						this.$emit('previous')
				}

				this.autoNav = false
			},
			ResetSteps() {
				this.scheduling.patient = null
				this.scheduling.partnership = null
				this.scheduling.module_id = null
				this.scheduling.modality_id = null
				this.scheduling.proceedings = []
				this.scheduling.schedules = []
				this.scheduling.time = null
				this.scheduling.classification = 1
				this.scheduling.places = null
				this.scheduling.obs = ''
				this.scheduling.journey_id = null
				this.scheduling.indicator = null
				this.scheduling.payment_info = null
				this.scheduling.instructions = ''
				this.scheduling.billing = null

				this.stepsMap = this.stepsMap.map(step => {
					return {
						...step,
						isValid: false,
					}
				})
			},
			async WalkStep(param = null) {
				const day_of_week = new Date().getDay()

				switch (this.step) {
					case 'pre':
						this.ResetSteps()
						this.currentStep = 1
						break
					case 'module':
						this.scheduling.module_id = param.id

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++
						break
					case 'modality':
						this.scheduling.modality_id = param.id

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++
						break
					case 'patient':
						this.scheduling.patient = param.patient

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++
						break
					case 'partnership':
						this.scheduling.partnership = param.partnership

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++
						break
					case 'guide':
						this.scheduling.billing = param.billing
						this.scheduling.indicator = param.indicator

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++
						break
					case 'proceedings':
						this.scheduling.proceedings = param.proceedings

						this.scheduling.schedules = _.orderBy(
							_.filter(param.schedules, schedule => {
								let dias_habilitados = JSON.parse(schedule.dias_habilitados)

								let allowed = false

								if (dias_habilitados) {
									if (schedule.ciclica) {
										allowed = dias_habilitados.find(dia => dia.value == day_of_week && dia.selected)

										allowed = typeof allowed === 'object'
									} else {
										let today = new Date()
										today = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1)
											.toString()
											.padStart(2, '0')}/${today.getFullYear()}`

										allowed =
											_.findIndex(dias_habilitados, {
												fulldate: today,
											}) > -1
									}
								}

								return !schedule.continua && schedule.status && allowed
							}),
							'nome'
						)

						if (this.scheduling.schedules.length > 0) {
							this.stepsMap[this.currentStep].isValid = true
							this.currentStep++
						} else {
							useError('NÃO HÁ AGENDAS DISPONÍVEIS PARA HOJE')
						}
						break
					case 'schedules':
						this.scheduling.time = param

						this.scheduling.origin = this.GetAttendanceOrigin()

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++
						break
					case 'attendance_priority':
						this.scheduling.classification = param.classification

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++
						break
					case 'confirm':
						this.scheduling.obs = param.obs
						this.scheduling.place = param.place
						this.scheduling.payment_info = param.payment_methods_info
						this.scheduling.instructions = param.instructions

						await this.SaveFit()
						break
				}

				this.autoNav = true
			},
			Continue() {
				if (this.isReceptionFlow && this.reception_flow_data?.is_finished) {
					this.FinishFlow()
					return
				}

				const refMap = {
					patient: 'patient_form',
					partnership: 'partnership',
					guide: 'guide',
					proceedings: 'proceedings',
					attendance_priority: 'attendance_priority',
					confirm: 'confirm',
				}

				const ref = refMap[this.step]

				if (ref && this.$refs[ref]?.Continue) {
					this.$refs[ref].Continue()
				}
			},
			GetAttendanceOrigin() {
				const ORIGIN_FIT = 4

				let origin = ORIGIN_FIT

				return origin
			},
			SaveFit() {
				this.StartLoading()

				delete this.scheduling.schedules

				return window.api
					.call('post', '/save-fit', {
						...this.scheduling,
						account_id: this.requester_selected_id,
					})
					.then(({ data }) => {
						if (data.response) {
							this.scheduling.journey_id = data.journey
							this.printStatement = true

							Swal.fire({
								icon: 'question',
								title: 'DESEJA IMPRIMIR OS DADOS DO ENCAIXE?',
								showConfirmButton: true,
								showCancelButton: true,
								confirmButtonText: 'SIM',
								cancelButtonText: 'NÃO',
								reverseButtons: true,
								customClass: {
									container: 'themed confirmation',
								},
							}).then(async res => {
								if (res.isConfirmed) {
									this.PrintTimeline()
								}

								this.printStatement = false

								useConfirmation()

								this.finishButtonLabel = 'CONCLUIR'
								this.reception_flow_data.is_finished = true
								this.currentStep = 0
								this.WalkStep()
							})
						} else {
							useError('HOUVE UM ERRO AO CONCLUIR, CONTATE O SUPORTE')
						}
					})
					.finally(() => {
						this.FinishLoading()
					})
			},
			PrintTimeline() {
				const fn = event => {
					if (event.data.print) {
						window.removeEventListener('message', fn)

						win.close()
					}
				}

				const win = window.open(`/pesquisa/imprimir?journey_id=${this.scheduling.journey_id}`, '_blank')
				win.mainWindow = window
				window.addEventListener('message', fn)
			},
			SetIndicator(indicator) {
				this.scheduling.indicator = indicator
			},
			NewFitToPatient() {
				if (this.patientsList.length !== 1) return

				this.WalkStep({
					patient: this.patientsList[0],
					autoWalkStep: true,
				})

				if (this.isReceptionFlow) {
					this.reception_flow_data.is_finished = false
					this.finishButtonLabel = 'CONTINUAR'
				}
			},
			PatientFormLoaded() {
				if (this.isReceptionFlow && this.reception_flow_data.patient_id && !this.scheduling.patient) {
					this.$refs.patient_form.SelectPatient(parseInt(this.reception_flow_data.patient_id))
				}
			},
		},
		async beforeMount() {
			await this.GetModulesAndModalities({
				with_instructions: true,
			})

			await this.HandleReceptionFlow()

			this.WalkStep()
		},
	}
</script>

<style lang="css" scoped>
	.grid-layout .main-content {
		max-height: calc(100vh - 12.775rem - var(--secondaryQuadrantHeight) * 2);
	}

	#payment-btn {
		height: 2.5rem;
	}
</style>
